// src/pages/PMSLoginPage.tsx
import React, { useState } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  useTheme,
  Tooltip,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
} from '@mui/material';
import { Visibility, VisibilityOff, VpnKey as KeyIcon, Business as BusinessIcon, Person as PersonIcon, LightMode, DarkMode, Hotel as HotelIcon } from '@mui/icons-material';
import { api } from '../services/api';
import { UserRole } from '../types/chat';
import { useTranslation } from 'react-i18next';

interface PMSHotelOption {
  id: string;
  name: string;
  code: string;
  pmsHotelId?: string;
}

// Flag country codes for languages
const languageFlags: Record<string, string> = {
  en: 'gb', // Great Britain
  tr: 'tr', // Turkey
  de: 'de', // Germany
  fr: 'fr', // France
  ru: 'ru', // Russia
  ar: 'ae', // UAE
};

// Flag icon component
const FlagIcon: React.FC<{ countryCode: string; size?: number }> = ({ countryCode, size = 24 }) => {
  return (
    <Box
      component="img"
      src={`https://flagcdn.com/w${size}/${countryCode}.png`}
      alt=""
      sx={{
        width: size,
        height: size * 0.75, // Maintain flag aspect ratio
        objectFit: 'cover',
        borderRadius: '2px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
        border: '0.5px solid rgba(0, 0, 0, 0.1)',
      }}
      onError={(e) => {
        // Fallback to SVG if PNG fails
        const target = e.target as HTMLImageElement;
        target.src = `https://flagcdn.com/${countryCode}.svg`;
      }}
    />
  );
};

const PMSLoginPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const accentColor = '#00AEEF';
  const accentHover = '#0095cf';
  const paperBg = isDark
    ? 'linear-gradient(135deg, rgba(15, 23, 42, 0.96) 0%, rgba(15, 23, 42, 0.88) 100%)'
    : 'linear-gradient(135deg, rgba(255, 255, 255, 0.97) 0%, rgba(255, 255, 255, 0.92) 100%)';
  const paperBorder = isDark ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid rgba(0, 0, 0, 0.04)';
  const primaryTextColor = isDark ? '#f8fafc' : '#1f2937';
  const secondaryTextColor = isDark ? 'rgba(248, 250, 252, 0.75)' : '#6b7280';
  const handleThemeToggle = () => {
    const newMode = isDark ? 'light' : 'dark';
    localStorage.setItem('themeMode', newMode);
    window.dispatchEvent(new CustomEvent('themeChange', { detail: newMode }));
  };
  
  const handleLanguageChange = (event: any) => {
    i18n.changeLanguage(event.target.value);
  };

  // Get the base language code (e.g., 'en' from 'en-US' or 'en-GB')
  const getLanguageCode = (lang: string) => {
    return lang.split('-')[0].toLowerCase();
  };

  const textFieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 3,
      backgroundColor: isDark ? 'rgba(15, 23, 42, 0.65)' : 'rgba(255, 255, 255, 0.95)',
      color: isDark ? '#f8fafc' : '#1f2937',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      '& fieldset': {
        borderColor: isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
        borderWidth: 1.5,
      },
      '&:hover': {
        backgroundColor: isDark ? 'rgba(15, 23, 42, 0.78)' : '#ffffff',
        boxShadow: isDark
          ? '0 8px 22px rgba(0, 0, 0, 0.45)'
          : '0 4px 12px rgba(0, 174, 239, 0.12)',
        '& fieldset': {
          borderColor: accentColor,
          borderWidth: 2,
        },
      },
      '&.Mui-focused': {
        backgroundColor: isDark ? 'rgba(15, 23, 42, 0.9)' : '#ffffff',
        boxShadow: isDark
          ? '0 12px 30px rgba(0, 174, 239, 0.28)'
          : '0 8px 20px rgba(0, 174, 239, 0.18)',
        '& fieldset': {
          borderColor: accentColor,
          borderWidth: 2.5,
        },
      },
    },
    '& .MuiInputLabel-root': {
      fontWeight: 600,
      color: isDark ? 'rgba(248, 250, 252, 0.7)' : '#6b7280',
      '&.Mui-focused': {
        color: accentColor,
        fontWeight: 700,
      },
    },
    '& .MuiInputBase-input': {
      fontWeight: 500,
      color: isDark ? '#f8fafc' : '#1f2937',
    },
  };
  const [hotelId, setHotelId] = useState('');
  const [pmsUsername, setPmsUsername] = useState('');
  const [pmsPassword, setPmsPassword] = useState('');
  const [showPmsPassword, setShowPmsPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [hotels, setHotels] = useState<PMSHotelOption[]>([]);
  const [hotelsLoading, setHotelsLoading] = useState(false);
  const [unknownReservationSending, setUnknownReservationSending] = useState(false);
  const [unknownReservationFeedback, setUnknownReservationFeedback] = useState<{
    type: 'success' | 'error';
    text: string;
  } | null>(null);

  React.useEffect(() => {
    const fetchHotels = async () => {
      setHotelsLoading(true);
      try {
        const [localResponse, pmsResponse] = await Promise.allSettled([
          api.get('/hotels/public/list'),
          api.get('/gsc-api/hotels'),
        ]);

        const localHotels = localResponse.status === 'fulfilled' ? localResponse.value.data : [];
        const pmsHotels = pmsResponse.status === 'fulfilled' ? pmsResponse.value.data : [];

        const map = new Map<string, PMSHotelOption>();

        // Index PMS hotels first by code (used to merge with local hotels)
        pmsHotels.forEach((pmsHotel: any) => {
          const pmsId = String(pmsHotel.id ?? pmsHotel.ID ?? '');
          map.set(pmsId, {
            id: pmsId,
            name: pmsHotel.name ?? pmsHotel.Name ?? '',
            code: pmsHotel.code ?? pmsHotel.Code ?? '',
            pmsHotelId: pmsId,
          });
        });

        // Override / enrich with local hotel names where the codes match
        localHotels.forEach((local: any) => {
          const matchByCode = Array.from(map.values()).find(
            h => h.code && local.code && h.code === local.code,
          );
          if (matchByCode) {
            matchByCode.name = local.name || matchByCode.name;
          } else {
            map.set(local.id, {
              id: local.id,
              name: local.name,
              code: local.code,
            });
          }
        });

        const list = Array.from(map.values())
          .filter(h => h.name && h.id)
          .sort((a, b) => a.name.localeCompare(b.name));
        setHotels(list);
      } catch (err) {
        console.error('[PMSLoginPage] Failed to load hotels:', err);
      } finally {
        setHotelsLoading(false);
      }
    };

    fetchHotels();
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    // Validate inputs
    if (!hotelId || !pmsUsername || !pmsPassword) {
      setError(t('pmsLogin.allFieldsRequired'));
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Use default API credentials
      const loginPayload: any = {
        apiUsername: 'HeyHotelAI',
        apiPassword: 'S44$!W6_E}V9w4t',
        hotelId,
        pmsUsername,
        pmsPassword,
      };
      
      const response = await api.post('/auth/pms-operator-login', loginPayload);
      
      const { user, accessToken, refreshToken } = response.data;
      localStorage.setItem('token', accessToken);
      localStorage.setItem('refreshToken', refreshToken);
      api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
      
      // Navigate based on user role
      const targetPath = user?.role === UserRole.SUPER_ADMIN ? '/super-admin' : '/dashboard';
      window.location.href = targetPath;
    } catch (error: any) {
      console.error('[PMSLoginPage] Login error:', error);
      const errorMessage = error.response?.data?.message 
        || error.response?.data?.error 
        || error.message 
        || t('pmsLogin.loginFailed');
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleNotifyUnknownReservation = async () => {
    setUnknownReservationFeedback(null);
    if (!hotelId?.trim()) {
      setUnknownReservationFeedback({ type: 'error', text: t('login.hotelRequiredError') });
      return;
    }
    setUnknownReservationSending(true);
    try {
      await api.post('/auth/notify-unknown-reservation', {
        hotelId: hotelId.trim(),
      });
      setUnknownReservationFeedback({ type: 'success', text: t('login.unknownReservationEmailSent') });
    } catch (err: any) {
      const msg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        t('login.unknownReservationEmailFailed');
      setUnknownReservationFeedback({
        type: 'error',
        text: typeof msg === 'string' ? msg : t('login.unknownReservationEmailFailed'),
      });
    } finally {
      setUnknownReservationSending(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 8, mb: 4 }}>
      <Paper
        elevation={3}
        sx={{
          p: 4,
          borderRadius: 3,
          background: paperBg,
          backdropFilter: 'blur(18px)',
          border: paperBorder,
          color: primaryTextColor,
          position: 'relative',
          boxShadow: isDark
            ? '0 25px 70px rgba(0, 0, 0, 0.6)'
            : '0 30px 80px rgba(15, 23, 42, 0.15)',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 16,
            right: 24,
            zIndex: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          <Tooltip title={isDark ? t('sidebar.light') : t('sidebar.dark')}>
            <IconButton
              onClick={handleThemeToggle}
              sx={{
                width: 42,
                height: 42,
                borderRadius: 2,
                border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(15, 23, 42, 0.1)'}`,
                backgroundColor: isDark ? 'rgba(255, 255, 255, 0.04)' : 'rgba(15, 23, 42, 0.05)',
                color: isDark ? '#f8fafc' : '#0f172a',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: 'rgba(0, 174, 239, 0.18)',
                  color: '#0f172a',
                },
              }}
            >
              {isDark ? <LightMode sx={{ fontSize: 22 }} /> : <DarkMode sx={{ fontSize: 22 }} />}
            </IconButton>
          </Tooltip>
          <FormControl
            size="small"
            sx={{
              width: 42,
              height: 42,
              minWidth: 42,
              minHeight: 42,
              padding: 0,
              margin: 0,
              boxSizing: 'border-box',
              borderRadius: 2,
              backgroundColor: isDark ? 'rgba(255, 255, 255, 0.04)' : 'rgba(15, 23, 42, 0.05)',
              border: isDark ? '1px solid rgba(255, 255, 255, 0.2)' : '1px solid rgba(15, 23, 42, 0.1)',
              transition: 'all 0.2s ease',
              '&:hover': {
                borderColor: accentColor,
                backgroundColor: isDark ? 'rgba(0, 174, 239, 0.18)' : 'rgba(0, 174, 239, 0.18)',
              },
              '& .MuiOutlinedInput-root': {
                width: '100%',
                height: '100%',
                minHeight: 'unset',
                borderRadius: 2,
                backgroundColor: 'transparent',
                border: 'none',
                padding: 0,
                margin: 0,
                boxSizing: 'border-box',
                '&.Mui-focused': {
                  border: 'none',
                  boxShadow: 'none',
                },
                '& .MuiSelect-icon': {
                  display: 'none',
                },
                '& fieldset': {
                  border: 'none',
                  display: 'none',
                },
              },
            }}
          >
            <Select
              value={i18n.language}
              onChange={handleLanguageChange}
              IconComponent={() => null}
              renderValue={(value) => {
                const langCode = getLanguageCode(value);
                const countryCode = languageFlags[langCode] || languageFlags[value] || 'gb';
                return (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
                    <FlagIcon countryCode={countryCode} size={20} />
                  </Box>
                );
              }}
              sx={{
                width: '100%',
                height: '100%',
                padding: 0,
                margin: 0,
                boxSizing: 'border-box',
                '& .MuiSelect-select': {
                  padding: '0 !important',
                  margin: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: '42px !important',
                  height: '42px !important',
                  width: '42px !important',
                  boxSizing: 'border-box',
                },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    borderRadius: 3,
                    mt: 1,
                    boxShadow: isDark ? '0 20px 40px rgba(0, 0, 0, 0.5)' : '0 8px 32px rgba(0, 0, 0, 0.12)',
                    border: isDark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 174, 239, 0.1)',
                    backdropFilter: 'blur(20px)',
                    backgroundColor: isDark ? 'rgba(15, 23, 42, 0.98)' : '#ffffff',
                    '& .MuiMenuItem-root': {
                      borderRadius: 2,
                      mx: 1,
                      my: 0.5,
                      '&:hover': {
                        backgroundColor: isDark ? 'rgba(0, 174, 239, 0.15)' : 'rgba(0, 174, 239, 0.08)',
                      },
                    },
                  },
                },
              }}
            >
              <MenuItem value="en">
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 1 }}>
                  <FlagIcon countryCode={languageFlags.en} size={32} />
                </Box>
              </MenuItem>
              <MenuItem value="tr">
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 1 }}>
                  <FlagIcon countryCode={languageFlags.tr} size={32} />
                </Box>
              </MenuItem>
              <MenuItem value="de">
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 1 }}>
                  <FlagIcon countryCode={languageFlags.de} size={32} />
                </Box>
              </MenuItem>
              <MenuItem value="fr">
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 1 }}>
                  <FlagIcon countryCode={languageFlags.fr} size={32} />
                </Box>
              </MenuItem>
              <MenuItem value="ru">
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 1 }}>
                  <FlagIcon countryCode={languageFlags.ru} size={32} />
                </Box>
              </MenuItem>
              <MenuItem value="ar">
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 1 }}>
                  <FlagIcon countryCode={languageFlags.ar} size={32} />
                </Box>
              </MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Box
          sx={{ textAlign: 'center', mb: 4 }}>
          <Box
              component="img"
              src="/heyhotelai-logo.png"
              alt="HeyHotel.AI"
              sx={{
                height: 60,
                width: 'auto',
                objectFit: 'contain',
              }}
            />
          <Typography
            variant="h4"
            component="h1"
            gutterBottom
            sx={{ fontWeight: 700, color: accentColor }}
          >
            {t('pmsLogin.title')}
          </Typography>
          <Typography variant="body2" sx={{ color: secondaryTextColor }}>
            {t('pmsLogin.subtitle')}
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {unknownReservationFeedback && (
          <Alert
            severity={unknownReservationFeedback.type === 'success' ? 'success' : 'error'}
            sx={{ mb: 3, borderRadius: 2.5 }}
            onClose={() => setUnknownReservationFeedback(null)}
          >
            {unknownReservationFeedback.text}
          </Alert>
        )}

        <form onSubmit={handleLogin}>
          <FormControl fullWidth margin="normal" required disabled={isLoading || hotelsLoading} sx={textFieldSx}>
            <InputLabel id="pms-hotel-select-label">{t('pmsLogin.hotelId')}</InputLabel>
            <Select
              labelId="pms-hotel-select-label"
              id="pms-hotel-select"
              value={hotelId}
              label={t('pmsLogin.hotelId')}
              onChange={(e) => setHotelId(e.target.value as string)}
              startAdornment={
                <InputAdornment position="start" sx={{ ml: 0.5 }}>
                  <BusinessIcon color="action" />
                </InputAdornment>
              }
              renderValue={(value) => {
                if (!value) {
                  return (
                    <Typography sx={{ color: secondaryTextColor, fontStyle: 'italic' }}>
                      {hotelsLoading ? '...' : t('login.none')}
                    </Typography>
                  );
                }
                const hotel = hotels.find(h => h.id === value);
                if (!hotel) {
                  return <Typography sx={{ fontWeight: 600 }}>{value}</Typography>;
                }
                return (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <HotelIcon sx={{ color: accentColor, fontSize: '1.25rem' }} />
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 600, color: primaryTextColor, lineHeight: 1.2 }}>
                        {hotel.name}
                      </Typography>
                      {hotel.code && (
                        <Typography variant="caption" sx={{ color: secondaryTextColor, fontSize: '0.7rem', lineHeight: 1.2 }}>
                          {hotel.code}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                );
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    maxHeight: 400,
                    borderRadius: 3,
                    mt: 1,
                    boxShadow: isDark ? '0 25px 60px rgba(0, 0, 0, 0.55)' : '0 8px 32px rgba(0, 0, 0, 0.12)',
                    border: isDark ? '1px solid rgba(255, 255, 255, 0.12)' : '1px solid rgba(0, 174, 239, 0.1)',
                    backdropFilter: 'blur(20px)',
                    backgroundColor: isDark ? 'rgba(15, 23, 42, 0.98)' : '#ffffff',
                  },
                },
              }}
            >
              {hotels.length === 0 && (
                <MenuItem value="" disabled>
                  <Typography sx={{ color: secondaryTextColor, fontStyle: 'italic' }}>
                    {hotelsLoading ? '...' : t('login.noHotelsAvailable') || ''}
                  </Typography>
                </MenuItem>
              )}
              {hotels.map((hotel) => (
                <MenuItem key={hotel.id} value={hotel.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 40,
                        height: 40,
                        borderRadius: 2,
                        backgroundColor: isDark ? 'rgba(0, 174, 239, 0.15)' : 'rgba(0, 174, 239, 0.12)',
                        color: accentColor,
                      }}
                    >
                      <HotelIcon sx={{ fontSize: '1.5rem' }} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: primaryTextColor,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {hotel.name}
                      </Typography>
                      {hotel.code && (
                        <Typography variant="caption" sx={{ color: secondaryTextColor, fontSize: '0.75rem', display: 'block', mt: 0.25 }}>
                          {hotel.code} {hotel.pmsHotelId ? `· ID: ${hotel.pmsHotelId}` : ''}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label={t('pmsLogin.pmsUsername')}
            value={pmsUsername}
            onChange={(e) => setPmsUsername(e.target.value)}
            margin="normal"
            required
            disabled={isLoading}
            sx={textFieldSx}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonIcon color="action" />
                </InputAdornment>
              ),
            }}
          />

          <TextField
            fullWidth
            label={t('pmsLogin.pmsPassword')}
            type={showPmsPassword ? 'text' : 'password'}
            value={pmsPassword}
            onChange={(e) => setPmsPassword(e.target.value)}
            margin="normal"
            required
            disabled={isLoading}
            sx={textFieldSx}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <KeyIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPmsPassword(!showPmsPassword)}
                    edge="end"
                    disabled={isLoading}
                  >
                    {showPmsPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Button
            type="button"
            fullWidth
            variant="text"
            size="medium"
            disabled={unknownReservationSending || hotelsLoading || isLoading}
            onClick={handleNotifyUnknownReservation}
            sx={{
              mt: 1,
              mb: 0.5,
              color: accentColor,
              fontWeight: 600,
              textTransform: 'none',
              '&:hover': {
                backgroundColor: isDark ? 'rgba(0, 174, 239, 0.08)' : 'rgba(0, 174, 239, 0.06)',
              },
            }}
          >
            {unknownReservationSending ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={18} sx={{ color: accentColor }} />
                {t('login.unknownReservationSending')}
              </Box>
            ) : (
              t('pmsLogin.notifyHotelButton')
            )}
          </Button>

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={isLoading}
            sx={{
              mt: 2,
              mb: 2,
              py: 1.5,
              backgroundColor: accentColor,
              boxShadow: '0 18px 35px rgba(0, 174, 239, 0.35)',
              '&:hover': {
                backgroundColor: accentHover,
                boxShadow: '0 18px 40px rgba(0, 174, 239, 0.45)',
              },
              '&:disabled': {
                backgroundColor: isDark ? 'rgba(255, 255, 255, 0.12)' : '#cbd5e1',
                color: isDark ? 'rgba(255, 255, 255, 0.6)' : '#94a3b8',
                boxShadow: 'none',
              },
            }}
          >
            {isLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              t('pmsLogin.login')
            )}
          </Button>
        </form>

        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Typography variant="body2" sx={{ color: secondaryTextColor }}>
            {t('pmsLogin.pmsIntegratedLogin')}
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default PMSLoginPage;



